import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { ApiError, type TournamentPreparationAccommodation, type TournamentPreparationVehicle } from "../../lib/api";
import { preparationApi, type TournamentSetupPayload } from "../../lib/preparationApi";

type VehicleDraft = Omit<TournamentPreparationVehicle, "baseName">;

function newId(prefix: string) {
  const token = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${token}`;
}

function stripVehicle(vehicle: TournamentPreparationVehicle): VehicleDraft {
  const { baseName: _baseName, ...rest } = vehicle;
  return { ...rest };
}

function newBase(): TournamentPreparationAccommodation {
  return { id: newId("base"), name: "Nowy nocleg", type: "house", address: "", notes: "", travelMinutes: 30, active: true };
}

function newVehicle(baseId: string): VehicleDraft {
  return { id: newId("vehicle"), name: "Nowe auto", capacity: 9, preferredCapacity: 8, baseId, setup: false, registration: "", notes: "", active: true, lockedDriverId: "", external: false };
}

export function LogisticsEditor({ tournamentId, data }: { tournamentId: string; data: TournamentSetupPayload }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [accommodations, setAccommodations] = useState<TournamentPreparationAccommodation[]>(() => data.accommodations.map((row) => ({ ...row })));
  const [vehicles, setVehicles] = useState<VehicleDraft[]>(() => data.vehicles.map(stripVehicle));
  const [keyPolicy, setKeyPolicy] = useState(data.tournament.vehicleKeyPolicy || "same-car");

  const reset = () => {
    setAccommodations(data.accommodations.map((row) => ({ ...row })));
    setVehicles(data.vehicles.map(stripVehicle));
    setKeyPolicy(data.tournament.vehicleKeyPolicy || "same-car");
  };

  const save = useMutation({
    mutationFn: () => preparationApi.saveLogistics(tournamentId, {
      expectedRevision: data.configRevision,
      accommodations,
      vehicles,
      vehicleKeyPolicy: keyPolicy,
    }),
    onSuccess: (next) => {
      queryClient.setQueryData(["tournament-setup", tournamentId], next);
      void queryClient.invalidateQueries({ queryKey: ["round-workspace", tournamentId] });
      void queryClient.invalidateQueries({ queryKey: ["generator-preflight", tournamentId] });
      setEditing(false);
    },
  });

  const error = save.error instanceof ApiError ? save.error.message : save.error instanceof Error ? save.error.message : "";
  const eligibleDrivers = useMemo(() => data.staff.filter((person) => person.driverEligible), [data.staff]);
  const assignedCounts = useMemo(() => new Map(data.accommodations.map((base) => [base.id, data.staff.filter((person) => person.baseId === base.id).length])), [data.accommodations, data.staff]);
  const dirty = JSON.stringify({ accommodations, vehicles, keyPolicy }) !== JSON.stringify({ accommodations: data.accommodations, vehicles: data.vehicles.map(stripVehicle), keyPolicy: data.tournament.vehicleKeyPolicy || "same-car" });

  const updateBase = (id: string, changes: Partial<TournamentPreparationAccommodation>) => setAccommodations((rows) => rows.map((row) => row.id === id ? { ...row, ...changes } : row));
  const updateVehicle = (id: string, changes: Partial<VehicleDraft>) => setVehicles((rows) => rows.map((row) => row.id === id ? { ...row, ...changes } : row));
  const addBase = () => setAccommodations((rows) => [...rows, newBase()]);
  const addVehicle = () => {
    const baseId = accommodations.find((row) => row.active)?.id || accommodations[0]?.id || "";
    setVehicles((rows) => [...rows, newVehicle(baseId)]);
  };

  const startAddBase = () => {
    setAccommodations([...data.accommodations.map((row) => ({ ...row })), newBase()]);
    setVehicles(data.vehicles.map(stripVehicle));
    setKeyPolicy(data.tournament.vehicleKeyPolicy || "same-car");
    setEditing(true);
  };

  const startAddVehicle = () => {
    const bases = data.accommodations.map((row) => ({ ...row }));
    const baseId = bases.find((row) => row.active)?.id || bases[0]?.id || "";
    setAccommodations(bases);
    setVehicles([...data.vehicles.map(stripVehicle), newVehicle(baseId)]);
    setKeyPolicy(data.tournament.vehicleKeyPolicy || "same-car");
    setEditing(true);
  };

  if (!editing) {
    return (
      <article className="panel logistics-editor-toolbar quick-actions">
        <div><strong>Konfiguracja logistyki</strong><span>r{data.configRevision} · {data.accommodations.length} noclegów · {data.vehicles.length} aut · kluczyki {data.tournament.vehicleKeyPolicy}</span></div>
        <div className="actions">
          <button type="button" className="primary-button" onClick={startAddBase}>+ Nocleg</button>
          <button type="button" className="primary-button" onClick={startAddVehicle} disabled={!data.accommodations.length}>+ Auto</button>
          <Link className="secondary-button" to="/tournaments/team">Import DOM / AUTO / ROLA</Link>
          <button type="button" className="secondary-button" onClick={() => { reset(); setEditing(true); }}>Edytuj wszystko</button>
        </div>
      </article>
    );
  }

  return (
    <>
      <article className="panel logistics-editor-toolbar">
        <div>
          <button type="button" className="secondary-button" onClick={addBase}>+ Nocleg</button>
          <button type="button" className="secondary-button" onClick={addVehicle} disabled={!accommodations.length}>+ Auto</button>
          <label className="prep-field logistics-key-policy"><span>Polityka kluczyków</span><select value={keyPolicy} onChange={(event) => setKeyPolicy(event.target.value)}><option value="free">Elastyczne</option><option value="same-car">Ten sam samochód</option><option value="same-driver">Ten sam kierowca</option></select></label>
        </div>
        <span>Zmiany są lokalne do kliknięcia „Zapisz logistykę”.</span>
      </article>

      <div className="logistics-edit-grid">
        {accommodations.map((base) => {
          const assigned = assignedCounts.get(base.id) || 0;
          const vehicleCount = vehicles.filter((vehicle) => vehicle.baseId === base.id).length;
          return (
            <article className="panel logistics-edit-card" key={base.id}>
              <header><div><span className="eyebrow">BASE · {assigned} OS. · {vehicleCount} AUT</span><h4>{base.name}</h4></div><div className="logistics-edit-actions"><button type="button" className={`logistics-mini-button ${base.active ? "active" : ""}`} onClick={() => updateBase(base.id, { active: !base.active })}>{base.active ? "AKTYWNY" : "WYŁĄCZONY"}</button><button type="button" className="logistics-mini-button danger" disabled={assigned > 0 || vehicleCount > 0} title={assigned || vehicleCount ? "Najpierw przenieś osoby i auta" : "Usuń nocleg"} onClick={() => setAccommodations((rows) => rows.filter((row) => row.id !== base.id))}>USUŃ</button></div></header>
              <div className="edit-fields">
                <label><span>Nazwa</span><input value={base.name} onChange={(event) => updateBase(base.id, { name: event.target.value })} /></label>
                <label><span>Typ</span><select value={base.type} onChange={(event) => updateBase(base.id, { type: event.target.value })}><option value="house">Dom / mieszkanie</option><option value="hotel">Hotel</option><option value="other">Inny</option></select></label>
                <label><span>Dojazd [min]</span><input type="number" min={0} max={240} value={base.travelMinutes} onChange={(event) => updateBase(base.id, { travelMinutes: Number(event.target.value) })} /></label>
                <label className="wide"><span>Adres</span><input value={base.address} onChange={(event) => updateBase(base.id, { address: event.target.value })} /></label>
                <label className="wide"><span>Notatka</span><textarea value={base.notes} onChange={(event) => updateBase(base.id, { notes: event.target.value })} /></label>
              </div>
            </article>
          );
        })}
      </div>

      <div className="logistics-edit-grid">
        {vehicles.map((vehicle) => (
          <article className="panel logistics-edit-card" key={vehicle.id}>
            <header><div><span className="eyebrow">VEHICLE · {vehicle.external ? "EXTERNAL" : vehicle.setup ? "SETUP" : "NORMAL"}</span><h4>{vehicle.name}</h4></div><div className="logistics-edit-actions"><button type="button" className={`logistics-mini-button ${vehicle.active ? "active" : ""}`} onClick={() => updateVehicle(vehicle.id, { active: !vehicle.active })}>{vehicle.active ? "AKTYWNE" : "WYŁĄCZONE"}</button><button type="button" className="logistics-mini-button danger" onClick={() => setVehicles((rows) => rows.filter((row) => row.id !== vehicle.id))}>USUŃ</button></div></header>
            <div className="edit-fields">
              <label><span>Nazwa</span><input value={vehicle.name} onChange={(event) => updateVehicle(vehicle.id, { name: event.target.value })} /></label>
              <label><span>Nocleg</span><select value={vehicle.baseId} onChange={(event) => updateVehicle(vehicle.id, { baseId: event.target.value })}>{accommodations.map((base) => <option key={base.id} value={base.id}>{base.name}</option>)}</select></label>
              <label><span>Capacity</span><input type="number" min={1} max={50} value={vehicle.capacity} onChange={(event) => { const capacity = Number(event.target.value); updateVehicle(vehicle.id, { capacity, preferredCapacity: Math.min(vehicle.preferredCapacity, capacity) }); }} /></label>
              <label><span>Preferred capacity</span><input type="number" min={1} max={vehicle.capacity} value={vehicle.preferredCapacity} onChange={(event) => updateVehicle(vehicle.id, { preferredCapacity: Number(event.target.value) })} /></label>
              <label><span>Typ</span><select value={vehicle.external ? "external" : vehicle.setup ? "setup" : "normal"} onChange={(event) => { const value = event.target.value; updateVehicle(vehicle.id, { setup: value === "setup", external: value === "external", ...(value === "external" ? { lockedDriverId: "" } : {}) }); }}><option value="normal">Normalne</option><option value="setup">Setup</option><option value="external">Autobus organizatora (bez naszego kierowcy)</option></select></label>
              {!vehicle.external ? (
                <label><span>Stały kierowca (rent)</span><select value={vehicle.lockedDriverId} onChange={(event) => updateVehicle(vehicle.id, { lockedDriverId: event.target.value })}><option value="">— brak (wolna pula) —</option>{eligibleDrivers.map((person) => <option key={person.personId} value={person.personId}>{person.displayName}</option>)}</select></label>
              ) : null}
              <label><span>Rejestracja</span><input value={vehicle.registration} onChange={(event) => updateVehicle(vehicle.id, { registration: event.target.value })} /></label>
              <label className="wide"><span>Notatka</span><textarea value={vehicle.notes} onChange={(event) => updateVehicle(vehicle.id, { notes: event.target.value })} /></label>
            </div>
          </article>
        ))}
      </div>

      {error ? <div className="prep-editor-error">{error}</div> : null}
      <article className="panel prep-save-strip">
        <div><strong>{dirty ? "Masz niezapisane zmiany" : "Brak zmian"}</strong><span>{accommodations.length} noclegów · {vehicles.length} aut · revision r{data.configRevision}</span></div>
        <div className="actions"><button type="button" className="secondary-button" onClick={() => { reset(); setEditing(false); }}>Anuluj</button><button type="button" className="primary-button" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Zapisuję…" : "Zapisz logistykę"}</button></div>
      </article>
    </>
  );
}
